import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { KpiCard, PageHeader, Panel, RangeSelect, EmptyState } from "@/components/admin/shared";
import { ConversionDialog } from "@/components/admin/ConversionDialog";
import { Button } from "@/components/ui/button";
import { Plus, PencilLine } from "lucide-react";

export default function Conversions() {
  const navigate = useNavigate();
  const [range, setRange] = useState("30days");
  const [convs, setConvs] = useState([]);
  const [team, setTeam] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    const [c, t] = await Promise.all([api.get(`/conversions?range=${range}`), api.get("/team")]);
    setConvs(c.data); setTeam(t.data);
  };
  useEffect(() => { load(); }, [range]);

  const total = convs.reduce((s, c) => s + (c.amount || 0), 0);
  const teamName = (id) => team.find((t) => t.member_id === id)?.name || "—";
  const pending = convs.filter((c) => c.details_pending).length;

  return (
    <div>
      <PageHeader title="Conversions & Sales" subtitle={`${convs.length} conversions${pending ? ` · ${pending} awaiting sale details` : ""}`}>
        <RangeSelect value={range} onChange={setRange} />
        <Button onClick={() => setDialog({})} data-testid="add-conversion-btn" className="h-10 rounded-xl" style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)", color: "#ffffff" }}><Plus size={16} /> Mark Converted</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard testid="conv-kpi-total" label="Total Conversions" value={convs.length} accent="#10B981" />
        <KpiCard testid="conv-kpi-value" label="Conversion Value" value={`₹${total.toLocaleString()}`} accent="#E8752A" />
        <KpiCard testid="conv-kpi-avg" label="Avg. Deal Size" value={`₹${convs.length ? Math.round(total / convs.length).toLocaleString() : 0}`} accent="#081226" />
      </div>

      <Panel>
        {convs.length === 0 ? <EmptyState text="No conversions in range" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-3 px-2">Visitor ID</th><th className="px-2">Name</th><th className="px-2">Membership</th><th className="px-2">Tenure</th><th className="px-2">Family</th><th className="px-2">Amount</th><th className="px-2">Sales Exec</th><th className="px-2">Source</th><th className="px-2">Date</th><th className="px-2"></th>
              </tr></thead>
              <tbody>
                {convs.map((c) => <ConvRow key={c.conversion_id} c={c} teamName={teamName} onOpen={() => navigate(`/admin/visitors/${c.visitor_id}`)} onEdit={() => setDialog(c)} />)}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <ConversionDialog open={!!dialog} onClose={() => setDialog(null)} initial={dialog?.visitor_id ? dialog : null} team={team} onSaved={load}
        title={dialog?.visitor_id ? `Sale Details — ${dialog.name}` : "Record Conversion"} />
    </div>
  );
}

const ConvRow = ({ c, teamName, onOpen, onEdit }) => (
  <tr data-testid="conversion-row" className="border-b border-slate-50 hover:bg-slate-50">
    <td className="py-3 px-2"><button onClick={onOpen} className="font-mono-num text-xs font-bold text-[#B8860B] hover:underline" data-testid="conversion-visitor-id">{c.visitor_id}</button></td>
    <td className="px-2 font-semibold text-[#081226]">{c.name}<p className="font-mono-num text-xs text-slate-400">{c.mobile}</p></td>
    <td className="px-2">
      {c.details_pending ? <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="conversion-pending-badge">Details pending</span>
        : <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.membership_type?.includes("Gold") ? "bg-[#E8752A]/15 text-[#B8860B]" : "bg-blue-100 text-blue-700"}`}>{c.membership_type}</span>}
    </td>
    <td className="px-2 text-slate-600 text-xs font-medium">{c.tenure || "—"}</td>
    <td className="px-2 text-slate-600 text-xs font-medium">{c.family_members || "—"}</td>
    <td className="px-2 font-mono-num font-bold text-emerald-700">₹{(c.amount || 0).toLocaleString()}</td>
    <td className="px-2 text-slate-500">{teamName(c.sales_executive)}</td>
    <td className="px-2 text-slate-500">{c.source}</td>
    <td className="px-2 text-slate-400 font-mono-num text-xs">{c.date?.slice(0, 10)}</td>
    <td className="px-2 text-right"><button onClick={onEdit} className={`p-2 ${c.details_pending ? "text-[#D95D1E]" : "text-slate-400"}`} title="Edit sale details" data-testid="conversion-edit-btn"><PencilLine size={16} /></button></td>
  </tr>
);
